/**
 * 点导入预览里的字卡弹出来的**读音浮窗**（2026-10-19 第二版；2026-10-20 加"跳转"）。
 *
 * ## 用户原话（验收标准）
 *
 * > "**点击那个字之后，就弹出一个浮窗**给他来选择，就不用跑到下面去……
 * >  **最好就地或者弹窗，有个窗来选择**。"
 *
 * > "我找到一个字并改了它的读音，之后**想找它另外一个的时候，就要不断地往下翻**……
 * >  **在浮窗里面加一个功能，直接定位到另外一个字**，让我能跳转过去。"
 *
 * > "浮窗里也要能看出**哪些位置已经标过音**（例如「第 3 行 · 已标 cháng」/
 * >  「第 7 行 · 未标音」）—— 帮他判断还剩哪个没标。"
 *
 * 于是原来「下方单独一块选读音」整块搬到这里，**不再两处并存**；
 * 「这个字还出现在」从**一行行文字**变成**可点的跳转按钮**（`HanziJumpLinks`）。
 * 里面按用户点名的几样东西排：
 *
 * | 区域 | 内容 |
 * | --- | --- |
 * | 这个字 | 大字 + `第 X 行第 Y 个字` + "这次出现几次" |
 * | 还出现在哪 | 同一个字在**别的**位置，每项带 `已标 cháng` / `未标音`，点了就跳过去 |
 * | 选读音 | `availableReadings()` 的每一个音，**点一下只是「选中」**（高亮，不关窗、不确认） |
 * | 确认 | 底部主按钮 `✅ 确认就是「zhǎng」`：文案跟着**选中的音**变；点它才确认并关窗 |
 * | 取消 | 右下角「取消」；Esc / 点遮罩 / 右上角 ✕ 同样关窗且不改任何东西 |
 *
 * ## 2026-10-20 第八轮：改成「**先选、再确认**」（用户原话）
 *
 * > "那些还需要确定的字，我核对了，点那个字、弹窗，如果音是对的，我就点确认，
 * >  这个没问题；**当音不对时，我要去点那个正确的音，点了之后，应该是选中，
 * >  然后确认按钮要改成相应的音，然后我再点确认**。你现在是点了那个音
 * >  **直接就确认并关闭弹窗**了。"
 *
 * 所以浮窗里只有一条确认路径：
 *
 *   1. **打开时默认选中**这个位置当前的读音（`reading ?? effectiveReading`）——
 *      音对时不用点读音，直接点底部按钮；
 *   2. 点任意一个读音 ⇒ **只是选中**（主色高亮，`aria-pressed`），**不关窗**；
 *   3. 底部按钮文案**跟着选中项变**：`✅ 确认就是「zhǎng」`；
 *   4. 点这个按钮 ⇒ `onConfirm(选中的音)` → 父组件重新预览 ⇒ 关窗。
 *      · 选中的就是当前音 ⇒ 只标记"确认过了"（`?` 字变浅绿）；
 *      · 选中的是另一个音 ⇒ **改读音 + 确认**（用户明确这也算确认）；
 *   5. 没有选中任何音（内置字表里没有这个字）⇒ 按钮**禁用**并写「先选一个读音」；
 *   6. 取消 / Esc / 遮罩 / ✕ ⇒ 什么都不改（`onClose` 一条路，⛔ 不碰 `onConfirm`）。
 *
 * ⚠️ **只有这一个浮窗改了**：浏览页那个 `HanziReadingsModal` 一个字都没动
 *    （用户点名"只改导入预览那个弹窗"）。
 *
 * ## 几个刻意的地方
 *
 *   · 预览网格**现在也显示拼音了**（2026-10-20 用户要逐字核对）；浮窗里显示的是
 *     这个字的**全部可选读音**，两者分工不同：卡片看"这一处读什么"，浮窗选"改成什么"。
 *   · 手机可点性：读音按钮、「确认」与「取消」都是 `size="md"`（`h-11` = **44px**）；
 *     「还出现在」的每一项也是 `min-h-[44px]`。
 *   · 选读音**不在这里发请求**：`onConfirm` 交给父组件（页面）去重新预览，
 *     本组件只负责显示、选中与回调。
 *   · 跳转**不在这里实现**：`onJumpToOther` 交给父组件（`ImportListingPreview`），
 *     它用与「浏览与编辑」**同一份** `useHanziJump` 关窗 → 滚过去 → 高亮 2 秒
 *     —— ⛔ **不打开**目标位置的浮窗（2026-10-20 用户要求）。
 *   · 父组件用 `importPreviewReadingPicker.ts` 的状态机决定"开 / 关"：
 *     点「✅ 确认就是「X」」→ 关窗 + 重新预览；「取消」→ 关窗、**一个音都不改**。
 */
import { useEffect, useState } from 'react';
import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';
import { availableReadings, describeReadingChoice } from '@fht/shared';
import type { ListingPositionInfo } from '@fht/shared';
import { HanziJumpLinks } from './HanziJumpLinks';
import type { HanziJumpCard } from './hanziJump';

/**
 * 打开浮窗时**默认选中**的音：这个位置已经标过就用它，否则用当前生效读音。
 *
 * ⚠️ 用户要求（2026-10-20 第八轮）："打开时选中当前这个位置的读音" ——
 *    这样"音对"时**不点读音、直接点确认**就行。
 * ⚠️ 纯函数、导出给单测（本仓 `apps/web` 只跑纯函数与源码守卫，不装 jsdom）。
 */
export function initialImportReadingSelection(position: ListingPositionInfo | null): string {
  if (position === null) return '';
  return position.reading !== '' ? position.reading : position.effectiveReading;
}

/**
 * 底部主按钮的文案：把**选中的音**写进去；没选中时写「先选一个读音」。
 *
 * ⚠️ 用户原话："确认按钮要改成相应的音，然后我再点确认"。
 */
export function importReadingConfirmLabel(selected: string): string {
  return selected === '' ? '先选一个读音' : `✅ 确认就是「${selected}」`;
}

export interface ImportReadingModalProps {
  /** 要选读音的那个位置；`null` = 浮窗关着。 */
  position: ListingPositionInfo | null;
  /** 同一个字在**别的**位置（不含当前这个；空 = 这次只出现一次）。 */
  others: readonly HanziJumpCard[];
  /** 正在重新预览（选完音之后）：读音按钮禁用，防止连点。 */
  busy: boolean;
  /**
   * 点了「✅ 确认就是「X」」：把**选中的那个音**写到这个位置上。
   *
   * ⚠️ 传出去的 `reading` 是浮窗里**当前选中的音**（默认 = 这个位置已有的读音 /
   *    `effectiveReading`）。父组件带同一个读音重新预览时，服务层把它当作
   *    **用户确认过**（见 `importDictation` 第 0 步）：`?` 的不确定标记失效、
   *    卡片变绿；如果选的是另一个音，则**同时改了读音**（这也算确认）。
   */
  onConfirm: (reading: string) => void;
  /** 取消 / Esc / 点遮罩 / ✕ —— **不改任何读音**。 */
  onClose: () => void;
  /**
   * 点了「还出现在」里的某一项：跳到那张卡（滚 + 高亮 2 秒；**不打开**它的浮窗）。
   *
   * ⚠️ 真正的滚/高亮在父组件（`useHanziJump`）—— 这里只把 key 交出去。
   */
  onJumpToOther?: ((key: string) => void) | undefined;
}

export function ImportReadingModal({
  position,
  others,
  busy,
  onConfirm,
  onClose,
  onJumpToOther,
}: ImportReadingModalProps) {
  const charText = position?.charText ?? '';
  const choices = position === null ? [] : availableReadings(charText);
  /**
   * **这个位置原来是什么音**（= 打开时的默认选中项）：
   * 已经标过就用它，否则用当前生效读音（= 库里的默认读音，卡片上显示的就是它）。
   */
  const currentReading = initialImportReadingSelection(position);
  /**
   * 浮窗里**当前选中的音**（用户点一个读音只改它，不关窗）。
   *
   * ⚠️ 初始值 = 这个位置当前的读音（用户要求"音对时直接点确认"）。
   * ⚠️ 换位置 / 关掉再打开时用下面的 effect 重选默认音；`positionKey` 只认
   *    "行 + 第几个字"，所以重新预览（同一个位置、读音变了）不会把用户
   *    正在点的选择冲掉。
   */
  const positionKey = position === null ? '' : `${position.line}|${position.indexInLine}`;
  const [selectedReading, setSelectedReading] = useState<string>(() =>
    initialImportReadingSelection(position),
  );
  useEffect(() => {
    setSelectedReading(initialImportReadingSelection(position));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 只跟着"位置"重选，见上方注释
  }, [positionKey]);

  return (
    <Modal
      open={position !== null}
      onClose={onClose}
      /** 用户要求「点遮罩也能关」；内容只是选音，误关不会有不可恢复的后果。 */
      closeOnBackdrop
      size="md"
      title={position === null ? '选读音' : `「${charText}」选读音`}
      description={
        position === null
          ? undefined
          : `${position.label} · ${
              position.occurrenceCount >= 2
                ? `这个字在这次粘贴里出现了 ${position.occurrenceCount} 次`
                : '这个字在这次粘贴里只出现了 1 次'
            }`
      }
    >
      {position === null ? null : (
        <div className="space-y-4">
          {/* ---------- 这个字 + 它的位置 ---------- */}
          <div className="flex items-center gap-3 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2">
            <span className="text-3xl font-semibold leading-none text-slate-900">{charText}</span>
            <div className="min-w-0">
              <p className="text-sm font-medium text-slate-800">{position.label}</p>
              <p className="mt-0.5 text-xs text-slate-600">
                要给<strong>这一个位置</strong>选读音
                {position.reading === '' ? '' : '（已经标过，可以改）'}。
              </p>
              {position.uncertain === true && (
                <p className="mt-1 rounded-lg bg-orange-100 px-2 py-1 text-xs font-medium text-orange-900">
                  文件里这个音的末尾标了「?」（生成文件的人不确定）—— 你看一眼：对就点下面的
                  <strong>「✅ 确认就是「…」」</strong>；不对就点上面另一个读音（只是选中），
                  再点确认。
                </p>
              )}
            </div>
          </div>

          {/*
            ---------- 这个字还出现在哪（**可点 → 跳过去**）----------
            ⚠️ 每一项都带"已标 / 未标音"：用户要据此判断还剩哪个没标。
          */}
          <HanziJumpLinks
            occurrences={others}
            onJump={(key) => onJumpToOther?.(key)}
            emptyText="没有别的位置了（这次只出现这一处）。"
          />

          {/* ---------- 可选读音：点一个**只是选中**（不关窗、不确认） ---------- */}
          <div>
            <p className="text-sm text-slate-800">{describeReadingChoice(charText, choices)}</p>
            {position.reading !== '' && (
              <p className="mt-1 text-xs text-slate-500">
                这个位置现在标的是：<strong>{position.reading}</strong>
              </p>
            )}
            {choices.length > 0 && (
              <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3">
                {choices.map((reading) => {
                  const selected = reading === selectedReading;
                  return (
                    <Button
                      key={reading}
                      size="md"
                      fullWidth
                      /** 选中项用主色高亮；**选中不等于确认**（不关窗）。 */
                      variant={selected ? 'primary' : 'secondary'}
                      disabled={busy}
                      onClick={() => setSelectedReading(reading)}
                      aria-pressed={selected}
                      aria-label={`选中「${charText}」在这个位置的读音 ${reading}`}
                    >
                      {reading}
                    </Button>
                  );
                })}
              </div>
            )}
            <p className="mt-2 text-xs text-slate-500">
              点一个读音只是<strong>选中</strong>（不关窗）：选好后点下面的
              <strong>「✅ 确认就是「…」」</strong>才会生效。
              {busy ? ' 正在重新预览…' : ''}
            </p>
            {/*
              ⚠️ 选中的音与这个位置原来的音不同 ⇒ 明说"确认后会改读音"：
                 用户 2026-10-20 的验收标准里，"选另一个音 + 确认"就是**改读音**。
            */}
            {selectedReading !== '' && selectedReading !== currentReading && (
              <p className="mt-1 text-xs text-amber-700">
                选中的音和这个位置现在的不一样：点确认会把这一处改成「{selectedReading}」。
              </p>
            )}
          </div>

          {/*
            ---------- 底部：**确认选中的音** + 取消 ----------
            ⚠️ 用户要求（2026-10-20 第八轮）："音不对时，点那个正确的音 → 是选中，
               然后**确认按钮要改成相应的音**，然后我再点确认"。
            ⚠️ 传出去的是**选中的音**（可能改了读音；同音 = 只确认）。
            ⚠️ 没有选中任何音时禁用并写「先选一个读音」（内置字表里没有这个字）。
          */}
          <div className="flex flex-col-reverse gap-2 border-t border-slate-200 pt-3 sm:flex-row sm:items-center sm:justify-between">
            <Button variant="secondary" size="md" onClick={onClose}>
              取消
            </Button>
            <Button
              variant="primary"
              size="md"
              disabled={busy || selectedReading === ''}
              /** ⚠️ **只有这里**会确认：把选中的音交给父组件（然后关窗 + 重新预览）。 */
              onClick={() => onConfirm(selectedReading)}
              /** 主按钮：确认**当前选中的**读音。 */
              aria-label={
                selectedReading === ''
                  ? `先给「${charText}」选一个读音，才能确认`
                  : `确认「${charText}」在这个位置的读音就是 ${selectedReading}`
              }
            >
              {importReadingConfirmLabel(selectedReading)}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
